import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Loader2, MessagesSquare, Star, UserRound } from "lucide-react";
import TopNav from "@/components/TopNav";
import { getSession } from "@/lib/api";
import { questionTopic } from "@/lib/interviewHighlights";

export default function InterviewTranscript() {
  const { sid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getSession(sid)
      .then(setData)
      .catch(() => setError("No se encontró la entrevista."));
  }, [sid]);

  const s = data?.session;
  const p = data?.progress;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="interview-page">
      <TopNav />
      <main className="mx-auto max-w-4xl px-6 py-12 lg:px-10">
        <p className="eyebrow flex items-center gap-1.5 text-accent-blue">
          <MessagesSquare className="h-3 w-3" /> Entrevista ABC · solo lectura
        </p>
        <h1 className="mt-3 font-display text-4xl font-medium sm:text-5xl">{s?.company || "Entrevista"}</h1>
        {p && (
          <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-slate-500" data-testid="interview-progress">
            {p.covered}/{p.total} preguntas cubiertas · {p.structure.departments} departamentos · {p.structure.people} cargos · {p.structure.activities} actividades
          </p>
        )}
        {error && <p className="mt-8 text-sm text-red-600">{error}</p>}
        {!s && !error && (
          <div className="mt-10 flex items-center gap-2 text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        )}
        {s && (
          <>
            <ol className="mt-10 space-y-5" data-testid="interview-transcript">
              {s.messages.map((m, i) => (
                <TranscriptLine key={`${m.ts}-${i}`} m={m} />
              ))}
            </ol>
            <Link
              to={`/informe/${sid}`}
              className="mt-10 inline-flex items-center gap-2 bg-slate-900 px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-slate-700"
              data-testid="interview-open-report"
            >
              Ver informe ejecutivo <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </>
        )}
      </main>
    </div>
  );
}

export function TranscriptLine({ m }) {
  const consultant = m.role === "consultant";
  const topic = questionTopic(m);
  const box = topic
    ? "border-accent-blue border-l-4 bg-accent-blue/5"
    : consultant ? "border-slate-200 bg-white" : "border-accent-blue/20 bg-white";
  return (
    <li className={`flex gap-3 ${consultant ? "" : "flex-row-reverse"}`} data-testid={topic ? "interview-question-highlight" : undefined}>
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center ${consultant ? "bg-slate-900 text-accent-blue-light" : "bg-accent-blue/10 text-accent-blue"}`}
      >
        {consultant ? <MessagesSquare className="h-3.5 w-3.5" /> : <UserRound className="h-3.5 w-3.5" />}
      </span>
      <div className={`max-w-[85%] border p-4 text-sm leading-relaxed ${box}`}>
        <p className="mb-1 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-400">
          {consultant ? "Consultor ABC" : "Cliente"}
          {topic && (
            <span className="inline-flex items-center gap-1 bg-accent-blue px-2 py-0.5 text-white" data-testid="interview-question-topic">
              <Star className="h-2.5 w-2.5" /> {topic}
            </span>
          )}
        </p>
        <p className="whitespace-pre-line text-slate-700">{m.content}</p>
      </div>
    </li>
  );
}
